import { useState } from "react";
import { Mail, Download, Loader2, Send, KeyRound, Package, ArrowLeft } from "lucide-react";
import { CONFIG } from "../config";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function MyOrders() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState("");
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState("");

  const onLookup = async (e) => {
    e.preventDefault();
    setError(""); setOrders([]); setResendMsg("");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/orders/lookup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok) setError(data.detail || "No orders found.");
      else setOrders(data.orders || []);
    } catch { setError("Network error."); }
    finally { setLoading(false); }
  };

  const onResend = async () => {
    setResending(true); setResendMsg("");
    try {
      const res = await fetch(`${API_BASE}/api/orders/resend`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();
      if (!res.ok) setResendMsg(data.detail || "Failed.");
      else setResendMsg(`Email sent to ${email}.`);
    } catch { setResendMsg("Network error."); }
    finally { setResending(false); }
  };

  return (
    <div data-testid="myorders-page" className="relative min-h-screen overflow-hidden">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[760px] h-[760px] gold-ring rounded-full pointer-events-none" />
      <div className="absolute inset-0 grain opacity-40 pointer-events-none" />

      <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <a href="/" className="inline-flex items-center gap-2 text-[12px] font-mono uppercase tracking-widest text-[color:var(--ink-mute)] hover:text-[color:var(--gold)] transition-colors mb-6">
          <ArrowLeft className="w-3.5 h-3.5" />Back to homepage
        </a>
        <h1 className="font-display font-black text-3xl sm:text-5xl leading-[1.05] tracking-tight">
          My <span className="text-[color:var(--gold)] glow-gold">Orders</span>
        </h1>
        <p className="mt-4 text-[14px] text-[color:var(--ink-dim)] max-w-2xl">
          Lost your license file or need to download the latest EA version? Enter your purchase email below.
        </p>

        <div className="mt-8 panel p-6 sm:p-8">
          <form onSubmit={onLookup} className="space-y-4">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)]">Purchase Email</span>
              <div className="mt-1.5 flex gap-2">
                <input type="email" placeholder="you@example.com" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="myorders-email"
                  className="flex-1 bg-[#0e0e12] border border-[#23232a] rounded-lg px-4 h-11 text-white placeholder:text-[color:var(--ink-mute)] focus:border-[color:var(--gold)]/60 focus:outline-none transition-colors"
                />
                <button type="submit" disabled={loading} data-testid="myorders-lookup"
                  className="btn-gold px-6 h-11 rounded-full inline-flex items-center justify-center gap-2 text-[13px] font-display whitespace-nowrap">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}Lookup
                </button>
              </div>
            </label>
            {error && <div data-testid="myorders-error" className="text-[13px] text-red-400 bg-red-500/5 border border-red-500/30 rounded-lg px-4 py-2.5">{error}</div>}
          </form>
        </div>

        {orders.length > 0 && (
          <div className="mt-6 space-y-4">
            <button onClick={onResend} disabled={resending} data-testid="myorders-resend"
              className="w-full h-11 rounded-full inline-flex items-center justify-center gap-2 text-[13px] font-display border border-[color:var(--gold)]/45 text-[color:var(--gold)] hover:bg-[color:var(--gold)]/10 transition-colors">
              {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Email me my license file again
            </button>
            {resendMsg && <div className="text-[13px] text-[color:var(--green)] bg-green-500/5 border border-green-500/30 rounded-lg px-4 py-2.5 text-center">{resendMsg}</div>}

            {orders.map((o) => (
              <div key={o.license_id} className="panel p-5 sm:p-6">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <KeyRound className="w-4 h-4 text-[color:var(--gold)]" />
                  <code className="font-mono font-bold text-[14px] text-[color:var(--gold)]">{o.license_id}</code>
                  {o.blocked && <span className="chip" style={{ color: "#f87171", borderColor: "rgba(248,113,113,.4)" }}>BLOCKED</span>}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)] mb-4">
                  Created {new Date(o.created_at).toLocaleDateString()} · {o.bound_count} / {o.max_accounts} accounts in use
                </div>
                <div className="grid sm:grid-cols-3 gap-2">
                  <a href={`${API_BASE}/api/download/zip/${encodeURIComponent(o.license_id)}`}
                    data-testid={`download-zip-${o.license_id}`}
                    className="btn-gold h-11 rounded-full inline-flex items-center justify-center gap-2 text-[12.5px] font-display">
                    <Package className="w-4 h-4" />ZIP (EA + license)
                  </a>
                  <a href={`${API_BASE}/api/download/ea/${encodeURIComponent(o.license_id)}`}
                    className="h-11 rounded-full inline-flex items-center justify-center gap-2 text-[12.5px] font-display border border-[color:var(--gold)]/45 text-[color:var(--gold)] hover:bg-[color:var(--gold)]/10 transition-colors">
                    <Download className="w-4 h-4" />.ex5 only
                  </a>
                  <a href={`${API_BASE}/api/download/license/${encodeURIComponent(o.license_id)}`}
                    className="h-11 rounded-full inline-flex items-center justify-center gap-2 text-[12.5px] font-display border border-[color:var(--gold)]/45 text-[color:var(--gold)] hover:bg-[color:var(--gold)]/10 transition-colors">
                    <Download className="w-4 h-4" />license.bin only
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-12 text-center font-mono text-[10px] uppercase tracking-widest text-[color:var(--ink-mute)]">
          Need help? WhatsApp +{(CONFIG.WHATSAPP || "").toString()}
        </div>
      </div>
    </div>
  );
}
